import { useState } from "react";
import { useParams, useLocation, Link } from "wouter";
import { useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  ArrowLeft,
  Save,
  Loader2,
  Sparkles,
  PencilLine,
  Wand2,
} from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { OfferForm, emptyOffer, type OfferFormValue } from "./offer-form";
import { OfferJsonImportDialog } from "@/components/offer-json-import-dialog";

const slugify = (s: string) =>
  s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

type Mode = "choose" | "ai" | "form";
// No inline drafting phase and no questions round: Generate is persist-first
// one-shot — the server creates the offering row as a generating draft, runs
// the full build (content + AI hero photo) in the background, and the user
// lands back on the Offerings grid, where an animated build card tracks it
// (offering-build-card.tsx). Same flow as Sales & Discounts — keep the two
// create pages in lockstep.

const EXAMPLE_PROMPT =
  "e.g. A 90-day done-with-you coaching program that helps early-stage founders land their first 10 paying customers through cold outreach and offer refinement. Delivered remotely with weekly calls.";

export default function OfferCreate() {
  const { brandId } = useParams<{ brandId: string }>();
  const [, navigate] = useLocation();
  const { toast } = useToast();

  const [mode, setMode] = useState<Mode>("choose");
  const [value, setValue] = useState<OfferFormValue>(emptyOffer());

  // ── AI flow state ──────────────────────────────────────────────────────
  const [description, setDescription] = useState("");
  const [submittingBuild, setSubmittingBuild] = useState(false);

  const createMutation = useMutation({
    mutationFn: async (payload: OfferFormValue) => {
      const body = { ...payload, slug: slugify(payload.name) };
      const res = await apiRequest("POST", `/api/brands/${brandId}/offers`, body);
      return res.json();
    },
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/offers`] });
      toast({ title: "Offering created" });
      navigate(`/brands/${brandId}/offers/${created.id}`);
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const canSave = !!value.name.trim() && !!value.offerType;

  // Persist-first generation: the server creates the offering row as a
  // generating draft and builds it in the background; we land the user on
  // the Offerings grid where the animated build card tracks it.
  async function handleGenerate() {
    const desc = description.trim();
    if (!desc || submittingBuild) return;
    setSubmittingBuild(true);
    try {
      await apiRequest("POST", `/api/brands/${brandId}/offering-builder/build`, { description: desc });
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/offers`] });
      toast({ title: "Building your offering", description: "Watch it come together in the grid." });
      navigate(`/brands/${brandId}/offers`);
    } catch (e: any) {
      setSubmittingBuild(false);
      toast({ title: "Couldn't start the build", description: e?.message || "Please try again.", variant: "destructive" });
    }
  }

  function startManual() {
    setValue(emptyOffer());
    setMode("form");
  }

  // ── Header ─────────────────────────────────────────────────────────────
  const header = (
    <div className="sticky top-0 z-10 -mx-8 px-8 py-2 bg-background/80 backdrop-blur border-b border-border/40 flex items-center justify-between gap-3">
      <Link href={`/brands/${brandId}/offers`}>
        <Button variant="ghost" size="sm" className="h-9 -ml-2 text-muted-foreground hover:text-foreground" data-testid="button-back">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to Offerings
        </Button>
      </Link>
      {mode === "form" && (
        <div className="flex items-center gap-2">
          <OfferJsonImportDialog current={value} onImport={setValue} />
          <Button size="sm" className="h-9 px-4 shadow-sm" onClick={() => createMutation.mutate(value)} disabled={!canSave || createMutation.isPending} data-testid="button-save-offer">
            {createMutation.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Save offering
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-4 w-full px-8">
      {header}

      {mode === "choose" && (
        <div className="max-w-3xl mx-auto pt-6">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-semibold">New Offering</h1>
            <p className="text-sm text-muted-foreground mt-1">How would you like to start?</p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Card
              className="relative cursor-pointer hover-elevate border-primary/40 ring-1 ring-primary/20"
              onClick={() => setMode("ai")}
              data-testid="card-mode-ai"
            >
              <CardContent className="p-6 space-y-3">
                <div className="inline-flex items-center justify-center h-11 w-11 rounded-lg bg-primary/10 text-primary">
                  <Sparkles className="h-6 w-6" />
                </div>
                <div>
                  <h2 className="text-base font-semibold flex items-center gap-2">
                    Build with AI
                    <span className="text-[10px] font-medium uppercase tracking-wide rounded-full bg-primary/15 text-primary px-2 py-0.5">Recommended</span>
                  </h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    Describe what you're building. We'll draft the full offering for you.
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card
              className="cursor-pointer hover-elevate"
              onClick={startManual}
              data-testid="card-mode-manual"
            >
              <CardContent className="p-6 space-y-3">
                <div className="inline-flex items-center justify-center h-11 w-11 rounded-lg bg-muted text-foreground">
                  <PencilLine className="h-6 w-6" />
                </div>
                <div>
                  <h2 className="text-base font-semibold">Enter manually</h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    Fill in the offering form yourself, field by field.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {mode === "ai" && (
        <div className="max-w-2xl mx-auto pt-4">
          <Card>
            <CardContent className="p-6 space-y-4">
              <div className="space-y-1">
                <h1 className="text-xl font-semibold flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-primary" /> Build with AI
                </h1>
                <p className="text-sm text-muted-foreground">
                  Describe your offering in your own words — the more detail, the better the draft.
                </p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">What are you thinking about building?</Label>
                <Textarea
                  rows={6}
                  autoFocus
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={EXAMPLE_PROMPT}
                  data-testid="input-ai-description"
                  onKeyDown={(e) => {
                    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") handleGenerate();
                  }}
                />
                <p className="text-xs text-muted-foreground">Tip: press ⌘/Ctrl + Enter to generate.</p>
              </div>
              <div className="flex items-center justify-between gap-2 pt-1">
                <Button variant="ghost" size="sm" onClick={() => setMode("choose")} data-testid="button-ai-back">
                  <ArrowLeft className="h-4 w-4 mr-2" /> Back
                </Button>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={startManual}
                    data-testid="button-switch-manual"
                  >
                    Enter manually instead
                  </Button>
                  <Button onClick={handleGenerate} disabled={!description.trim() || submittingBuild} data-testid="button-generate">
                    {submittingBuild ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Wand2 className="h-4 w-4 mr-2" />} Generate offering
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {mode === "form" && (
        <Card className="border-border/40">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-4 gap-3">
              <h1 className="text-xl font-semibold">New Offering</h1>
            </div>
            <OfferForm value={value} onChange={setValue} />
          </CardContent>
        </Card>
      )}
    </div>
  );
}
